import { Typography, Table, Tag, Card, Collapse, Space, Alert, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface Endpoint {
  method: string;
  path: string;
  description: string;
  auth: string;
}

const methodColor: Record<string, string> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const columns = [
  {
    title: 'Method',
    dataIndex: 'method',
    width: 80,
    render: (m: string) => <Tag color={methodColor[m] || 'default'}>{m}</Tag>,
  },
  {
    title: 'Endpoint',
    dataIndex: 'path',
    render: (p: string) => <Text code copyable={{ text: p }}>{p}</Text>,
  },
  {
    title: 'Description',
    dataIndex: 'description',
  },
  {
    title: 'Auth',
    dataIndex: 'auth',
    width: 120,
    render: (a: string) => {
      if (a === 'none') return <Tag>None</Tag>;
      if (a === 'admin') return <Tag color="red">Admin</Tag>;
      return <Tag color="blue">Admin / Readonly</Tag>;
    },
  },
];

const searchEndpoints: Endpoint[] = [
  { method: 'POST', path: '/api/v1/search', description: 'Search products by product_id, mpn, sku, upc, ingram_sku, description, category, or manufacturer', auth: 'any' },
];

const productEndpoints: Endpoint[] = [
  { method: 'GET', path: '/api/v1/products', description: 'List recent products. Params: page_size, source (all/external/etilize)', auth: 'any' },
  { method: 'GET', path: '/api/v1/autocomplete/manufacturers', description: 'Manufacturer name autocomplete. Params: q, limit', auth: 'any' },
  { method: 'GET', path: '/api/v1/autocomplete/categories', description: 'Category name autocomplete. Params: q, limit', auth: 'any' },
  { method: 'GET', path: '/api/v1/stats', description: 'Database statistics (product counts, manufacturers, categories)', auth: 'any' },
];

const accountEndpoints: Endpoint[] = [
  { method: 'PUT', path: '/api/v1/users/me/password', description: 'Change own password. Body: current_password, new_password', auth: 'any' },
];

const sections = [
  { key: 'search', label: 'Search', data: searchEndpoints },
  { key: 'products', label: 'Products & Autocomplete', data: productEndpoints },
  { key: 'account', label: 'Account', data: accountEndpoints },
];

export function ApiGuideUserPage() {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0 }}>API Reference</Title>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href="/FN-PIM_API_Documentation.md"
          download="FN-PIM_API_Documentation.md"
        >
          Download Full Docs
        </Button>
      </div>

      <Card size="small" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginTop: 0 }}>Authentication</Title>
        <Paragraph>
          All API requests (except <Text code>/authenticate</Text> and <Text code>/health</Text>) require authentication via one of:
        </Paragraph>
        <Space direction="vertical" size={4}>
          <Text><Text strong>JWT Token:</Text> POST to <Text code>/authenticate</Text> with <Text code>{`{"username": "...", "password": "..."}`}</Text>. The token is returned as a cookie and in the response body.</Text>
          <Text><Text strong>API Key (Bearer):</Text> <Text code>Authorization: Bearer pim_xxxxxxxx</Text></Text>
          <Text><Text strong>API Key (Basic):</Text> <Text code>Authorization: Basic base64(username:pim_xxxxxxxx)</Text></Text>
        </Space>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="Legacy prefix"
          description={<>All routes are also available under <Text code>/spex/plus/</Text> for backward compatibility (e.g. <Text code>/spex/plus/search</Text>).</>}
        />
      </Card>

      <Card size="small" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginTop: 0 }}>Search Example</Title>
        <Paragraph>
          <Text code copyable>
            {`curl -X POST https://pim.flatironnetworks.net/api/v1/search -H "Authorization: Bearer pim_YOUR_KEY" -H "Content-Type: application/json" -d '{"upc": "197528497839"}'`}
          </Text>
        </Paragraph>
        <Paragraph>
          Search fields (send one per request): <Text code>product_id</Text>, <Text code>mpn</Text>, <Text code>sku</Text>, <Text code>upc</Text>, <Text code>ingram_sku</Text>, <Text code>description</Text>, <Text code>category</Text>, <Text code>manufacturer</Text>
        </Paragraph>
        <Paragraph>
          Optional: <Text code>page_size</Text> (default 10), <Text code>include_alternatives</Text> (boolean)
        </Paragraph>
      </Card>

      <Card size="small" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginTop: 0 }}>Response Format</Title>
        <Paragraph>
          Search returns a JSON array of product objects. Key fields:
        </Paragraph>
        <Table
          dataSource={[
            { _key: 1, field: 'productid', type: 'string', desc: 'Provider-specific product ID' },
            { _key: 2, field: 'data_source', type: 'string', desc: 'Provider name (icecat, etilize, external, aggregated)' },
            { _key: 3, field: 'quality_score', type: 'number', desc: 'Data completeness score (0-100)' },
            { _key: 4, field: 'mfgpartno', type: 'string', desc: 'Manufacturer part number' },
            { _key: 5, field: 'description', type: 'string', desc: 'Product name/title' },
            { _key: 6, field: 'short_description', type: 'string|null', desc: 'Brief summary' },
            { _key: 7, field: 'long_description', type: 'string|null', desc: 'Full description (may contain HTML)' },
            { _key: 8, field: 'manufacturer', type: 'object', desc: '{name, manufacturerid}' },
            { _key: 9, field: 'category_name', type: 'string|null', desc: 'Product category' },
            { _key: 10, field: 'image', type: 'string|null', desc: 'Primary image URL' },
            { _key: 11, field: 'features', type: 'array', desc: '[{text, ordernumber}]' },
            { _key: 12, field: 'attributes', type: 'array', desc: '[{attribute_name, displayvalue, unit_name, feature_group}]' },
            { _key: 13, field: 'skus', type: 'array', desc: '[{name, sku}] — UPC, EAN, etc.' },
            { _key: 14, field: 'elements', type: 'array', desc: '[{type, url, thumbnail, propertykey}] — images, PDFs' },
          ]}
          rowKey="_key"
          columns={[
            { title: 'Field', dataIndex: 'field', render: (f: string) => <Text code>{f}</Text> },
            { title: 'Type', dataIndex: 'type', width: 120 },
            { title: 'Description', dataIndex: 'desc' },
          ]}
          size="small"
          pagination={false}
        />
      </Card>

      <Collapse
        defaultActiveKey={['search', 'products']}
        items={sections.map((s) => ({
          key: s.key,
          label: <Text strong>{s.label} ({s.data.length})</Text>,
          children: (
            <Table
              dataSource={s.data.map((e, i) => ({ ...e, _key: i }))}
              rowKey="_key"
              columns={columns}
              size="small"
              pagination={false}
            />
          ),
        }))}
      />
    </div>
  );
}
